// New API client to replace Appwrite
// Use relative URLs if REACT_APP_API_URL is not set (for production with same-domain API)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

// Helper function to set auth token
const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
    const error = new Error(errorMessage);
    error.status = response.status;
    throw error;
  }

  // Handle 204 No Content - no body to parse
  if (response.status === 204) {
    return null;
  }

  // Try to parse JSON, but handle empty responses gracefully
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }

  try {
    const parsed = JSON.parse(text);
    return parsed;
  } catch (e) {
    // If parsing fails, log the error and return null
    console.error('Failed to parse JSON response:', e, 'Response text:', text.substring(0, 200));
    return null;
  }
};

// Config object to maintain compatibility
export const config = {
  endpoint: API_BASE_URL,
  testimonialCollectionId: 'reviews', // Map to reviews endpoint
  photosCollectionId: 'images', // Map to images endpoint
  photosBucketId: 'images', // Map to images endpoint
  userCollectionId: 'users', // Map to users endpoint
  permissionsCollectionId: 'permissions', // Placeholder - not used in new system
};

// Get all documents (reviews or images)
export const getAllDocuments = async (collectionId, limit, filters = []) => {
  try {
    let endpoint = '';
    
    // Map collection IDs to endpoints
    if (collectionId === config.testimonialCollectionId || collectionId === 'reviews') {
      endpoint = '/api/reviews';
      // Handle filters
      if (filters.length > 0) {
        const filter = filters[0];
        const [key, value] = Object.entries(filter)[0];
        if (key === 'status') {
          endpoint += `?status=${encodeURIComponent(value)}`;
        }
      }
    } else if (collectionId === config.photosCollectionId || collectionId === 'images') {
      endpoint = '/api/images';
    } else if (collectionId === config.userCollectionId || collectionId === 'users') {
      endpoint = '/api/users';
    } else {
      throw new Error(`Unknown collection: ${collectionId}`);
    }

    const data = await apiRequest(endpoint);
    const result = Array.isArray(data) ? data : [];
    console.log(`getAllDocuments: endpoint=${endpoint}, returned ${result.length} items`);
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to retrieve documents');
  }
};

// Get single document
export const getDocument = async (collectionId, documentId) => {
  try {
    let endpoint = '';
    
    if (collectionId === config.testimonialCollectionId || collectionId === 'reviews') {
      endpoint = `/api/reviews/${documentId}`;
    } else if (collectionId === config.photosCollectionId || collectionId === 'images') {
      endpoint = `/api/images/${documentId}`;
    } else if (collectionId === config.userCollectionId || collectionId === 'users') {
      endpoint = `/api/users/${documentId}`;
    } else {
      throw new Error(`Unknown collection: ${collectionId}`);
    }

    return await apiRequest(endpoint);
  } catch (error) {
    throw new Error(`Failed to retrieve document: ${error.message}`);
  }
};

// Upload/create document
export async function uploadData(data, collectionId) {
  try {
    let endpoint = '';
    let body = {};

    if (collectionId === config.testimonialCollectionId || collectionId === 'reviews') {
      endpoint = '/api/reviews';
      body = {
        firstName: data.firstName,
        lastInitial: data.lastInitial,
        review: data.review,
        status: data.status || 'Waiting for Review',
      };
      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return response;
    } else if (collectionId === config.photosCollectionId || collectionId === 'images') {
      // For images, uploadData should not be used directly - use uploadFile instead
      // This is a fallback for cases where only metadata is provided
      endpoint = '/api/images';
      // Note: This endpoint requires a file, so this will likely fail
      // But we'll handle it gracefully
      throw new Error('For images, use uploadFile() instead of uploadData()');
    } else if (collectionId === config.userCollectionId || collectionId === 'users') {
      endpoint = '/api/users';
      body = data;
      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return response;
    } else {
      throw new Error(`Unknown collection: ${collectionId}`);
    }
  } catch (error) {
    throw new Error(error.message || 'Failed to upload data');
  }
}

// Upload file
export async function uploadFile(file, bucketId, imageId = null, imageName = null) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', imageName || file.name || 'uploaded-file');

    const token = getAuthToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // If imageId is provided, update existing image; otherwise create new
    // Convert $id or id to numeric ID if needed
    let numericId = null;
    if (imageId) {
      // If it's a string that looks like a number, parse it
      // Otherwise, if it's an object with id or $id, extract it
      if (typeof imageId === 'object' && imageId !== null) {
        numericId = imageId.id || imageId.$id;
      } else {
        numericId = imageId;
      }
      // Convert to number if it's a string representation of a number
      if (typeof numericId === 'string' && /^\d+$/.test(numericId)) {
        numericId = parseInt(numericId, 10);
      }
    }

    const method = numericId ? 'PUT' : 'POST';
    const endpoint = numericId ? `/api/images/${numericId}` : '/api/images';

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    const data = await response.json();
    return data.photoUrl; // Return the photoUrl for compatibility
  } catch (error) {
    console.error('Failed to upload file:', error);
    throw error;
  }
}

// Delete file
export async function deleteFile(url) {
  try {
    // Extract image ID from URL
    const urlParts = url.split('/');
    const imageId = urlParts[urlParts.length - 2] || urlParts[urlParts.length - 1];
    
    // If it's a full URL, try to extract the ID
    const match = url.match(/\/images\/(\d+)/);
    const id = match ? match[1] : imageId;

    await apiRequest(`/api/images/${id}`, {
      method: 'DELETE',
    });

    return null;
  } catch (error) {
    console.error('Failed to delete file:', error);
    throw error;
  }
}

// Update document
export const updateDocument = async (id, data, collectionId) => {
  try {
    // Convert $id or id to numeric ID if needed
    let numericId = id;
    if (typeof id === 'object' && id !== null) {
      numericId = id.id || id.$id;
    }
    // Convert to number if it's a string representation of a number
    if (typeof numericId === 'string' && /^\d+$/.test(numericId)) {
      numericId = parseInt(numericId, 10);
    }

    let endpoint = '';
    let body = {};

    if (collectionId === config.testimonialCollectionId || collectionId === 'reviews') {
      endpoint = `/api/reviews/${numericId}`;
      body = {
        firstName: data.firstName,
        lastInitial: data.lastInitial,
        review: data.review,
        status: data.status,
      };
      await apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    } else if (collectionId === config.photosCollectionId || collectionId === 'images') {
      // For images, just update metadata (file uploads are handled separately via uploadFile)
      endpoint = `/api/images/${numericId}`;
      body = {
        name: data.name,
      };
      // Only update if name is provided
      if (data.name !== undefined) {
        await apiRequest(endpoint, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      }
    } else if (collectionId === config.userCollectionId || collectionId === 'users') {
      endpoint = `/api/users/${numericId}`;
      body = data;
      await apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    } else {
      throw new Error(`Unknown collection: ${collectionId}`);
    }

    return null;
  } catch (error) {
    throw new Error(error.message || 'Failed to update document');
  }
};

// Delete document
export const deleteDocument = async (documentId, collectionId) => {
  try {
    // Convert $id or id to numeric ID if needed
    let numericId = documentId;
    if (typeof documentId === 'object' && documentId !== null) {
      numericId = documentId.id || documentId.$id;
    }
    // Convert to number if it's a string representation of a number
    if (typeof numericId === 'string' && /^\d+$/.test(numericId)) {
      numericId = parseInt(numericId, 10);
    }

    let endpoint = '';

    if (collectionId === config.testimonialCollectionId || collectionId === 'reviews') {
      endpoint = `/api/reviews/${numericId}`;
    } else if (collectionId === config.photosCollectionId || collectionId === 'images') {
      endpoint = `/api/images/${numericId}`;
    } else if (collectionId === config.userCollectionId || collectionId === 'users') {
      endpoint = `/api/users/${numericId}`;
    } else {
      throw new Error(`Unknown collection: ${collectionId}`);
    }

    await apiRequest(endpoint, {
      method: 'DELETE',
    });

    return null;
  } catch (error) {
    throw new Error(error.message || 'Failed to delete document');
  }
};

// Auth functions
export async function createUser(email, password, username) {
  try {
    const response = await apiRequest('/api/users/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, username }),
    });

    // Store token
    if (response.token) {
      setAuthToken(response.token);
    }

    return response.user;
  } catch (error) {
    throw new Error(error.message || 'Failed to create user');
  }
}

export async function signIn(email, password) {
  try {
    const response = await apiRequest('/api/users/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Check if response is valid
    if (!response) {
      throw new Error('Invalid response from server');
    }

    // Store token
    if (response.token) {
      setAuthToken(response.token);
    }

    // Check if user object exists
    if (!response.user) {
      throw new Error('User data not found in response');
    }

    return { $id: response.user.accountId || response.user.$id, ...response.user };
  } catch (error) {
    console.error('SignIn error:', error);
    throw new Error(error.message || 'Failed to sign in');
  }
}

export async function getAccount() {
  try {
    return await apiRequest('/api/users/me');
  } catch (error) {
    // If 401 (Unauthorized), return null instead of throwing - user is just not logged in
    if (error.status === 401) {
      return null;
    }
    throw new Error(error.message || 'Failed to get account');
  }
}

export async function getCurrentUser() {
  try {
    const account = await getAccount();
    if (!account) {
      return null; // User is not logged in
    }

    // Return in Appwrite-compatible format
    return {
      $id: account.accountId || account.id,
      accountId: account.accountId,
      email: account.email,
      username: account.username,
      avatar: account.avatar,
      permissions: account.permissions,
    };
  } catch (error) {
    console.log('Get current user error:', error);
    return null;
  }
}

export const signOut = async () => {
  try {
    setAuthToken(null);
    return { success: true };
  } catch (error) {
    throw new Error(error.message || 'Failed to sign out');
  }
};

// Permission functions (stub for now - can be implemented later)
export const uploadPermissionRecord = async (permissionName, customAccess, pageAccess) => {
  // TODO: Implement if needed
  console.warn('uploadPermissionRecord not yet implemented');
  return null;
};

export const getPermissionGroups = async () => {
  // TODO: Implement if needed
  console.warn('getPermissionGroups not yet implemented');
  return [];
};

// Stub functions for components that may not be actively used
export const updateProduct = async (id, data) => {
  console.warn('updateProduct not yet implemented');
  return null;
};

export const getAllInventory = async () => {
  console.warn('getAllInventory not yet implemented');
  return [];
};

export const uploadSalesOrder = async (data) => {
  console.warn('uploadSalesOrder not yet implemented');
  return null;
};

export const updateSalesOrder = async (id, data) => {
  console.warn('updateSalesOrder not yet implemented');
  return null;
};

